/**
 * @fileoverview Data Phase — per-agent detail route
 *
 * URL: /dashboard/jobs/:jobId/summaries/:agent
 *
 * The `$agent` segment is a stable slug from `agentRegistry.ts`
 * (e.g. "corporate", "loans", "ato-sro"). The route:
 *
 *   1. Validates the slug → throws to /summaries if unknown.
 *   2. Looks up the corresponding `summarizationTasks` row by manifest id
 *      for the active job.
 *   3. If a task exists, dispatches to the per-agent detail view via
 *      `AgentDetailRouter`.
 *   4. If no task exists yet (the agent hasn't been triggered by any
 *      documents), renders a "waiting for documents" placeholder so the
 *      page is reachable from the sidebar even before first run.
 */

import { useEffect, useMemo } from "react";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  convexQuery,
  useConvexAction,
  useConvexMutation,
} from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { toast } from "sonner";
import { ArrowLeft, Plus } from "lucide-react";
import { Button } from "@/ui/primitives/button";
import { AgentDetailRouter } from "@/components/jobs/phases/gather/summarization/AgentDetailRouter";
import {
  DATA_AGENTS_BY_SLUG,
  slugToAgentId,
  isDataAgentVisibleOnJob,
  dataAgentLabel,
} from "@/components/jobs/phases/gather/summarization/agentRegistry";
import { caseTypeFromAppointmentType } from "@/lib/precedent-templates";

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/jobs/$jobId/summaries/$agent",
)({
  validateSearch: (search: Record<string, unknown>) => ({
    from: typeof search.from === "string" ? search.from : undefined,
    threadId: typeof search.threadId === "string" ? search.threadId : undefined,
  }),
  beforeLoad: ({ params }) => {
    if (!DATA_AGENTS_BY_SLUG.has(params.agent)) {
      throw redirect({
        to: "/dashboard/jobs/$jobId/summaries",
        params: { jobId: params.jobId },
      });
    }
  },
  component: AgentDetailPage,
});

function AgentDetailPage() {
  const { jobId, agent: slug } = Route.useParams();
  const navigate = useNavigate();
  const { from, threadId } = Route.useSearch();
  const cameFromChat = from === "chat";

  // Slug → agentId. Guarded by `beforeLoad`, so this lookup is always defined
  // by the time the component renders, but we narrow defensively anyway.
  const agentEntry = DATA_AGENTS_BY_SLUG.get(slug);
  const agentId = slugToAgentId(slug);

  const { data: job } = useQuery(
    convexQuery(api.jobs.queries.getJob, { jobId: jobId as Id<"jobs"> }),
  );
  const isViewer = job?.userRole === "viewer";
  // Personal-insolvency jobs relabel the Corporate agent as "Structure".
  const isBankruptcy =
    caseTypeFromAppointmentType(job?.appointmentType) === "personal";

  // Guard typed URLs / stale bookmarks against agents this job can't show:
  // pre-appointment jobs only expose Corporate (bounce to it), and bankruptcy
  // jobs hide company-only agents like board reports (bounce to the Data
  // overview, which never disappears).
  useEffect(() => {
    if (!job) return;
    if (isDataAgentVisibleOnJob(slug, job.status, job.appointmentType)) return;
    if (job.status === "pre_appointment") {
      void navigate({
        to: "/dashboard/jobs/$jobId/summaries/$agent",
        params: { jobId, agent: "corporate" },
        replace: true,
      });
      return;
    }
    void navigate({
      to: "/dashboard/jobs/$jobId/summaries",
      params: { jobId },
      replace: true,
    });
  }, [job, slug, jobId, navigate]);

  const { data: tasksData } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.getSummarizationTasksByJob,
      {
        jobId: jobId as Id<"jobs">,
      },
    ),
  );

  // Pluck the task row matching this agent's manifest id, if one exists yet.
  const task = useMemo(
    () =>
      (tasksData ?? []).find((t: { agentId: string }) => t.agentId === agentId),
    [tasksData, agentId],
  );

  const { mutateAsync: rerunAgent } = useMutation({
    mutationFn: useConvexAction(
      api.jobs.summarization.actions.rerunSummarizationAgent,
    ),
  });

  const startTaskMutation = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.taskState.startAgentTask,
    ),
  });
  const startTask = startTaskMutation.mutateAsync;

  const onBack = () => {
    if (cameFromChat) {
      navigate({
        to: "/dashboard/jobs/$jobId/agent",
        params: { jobId },
        search: threadId ? { threadId } : {},
      });
    } else {
      navigate({
        to: "/dashboard/jobs/$jobId/summaries",
        params: { jobId },
      });
    }
  };

  // Opens the agent's workspace before any document has triggered it, so the
  // practitioner can record entries by hand. The task query is reactive, so
  // creating the row swaps this placeholder for the real view.
  const handleStartManualEntry = async () => {
    if (!agentId) return;
    try {
      await startTask({ jobId: jobId as Id<"jobs">, agentId });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not open this agent");
    }
  };

  const handleRerun = async () => {
    if (!task) return;
    try {
      await rerunAgent({
        taskId: task._id as Id<"summarizationTasks">,
        jobId: jobId as Id<"jobs">,
      });
      toast.success("Agent re-running");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to re-run agent");
    }
  };

  // No task row yet — show a friendly placeholder. The agent will run
  // automatically once a triggering document is indexed for one of its
  // categories.
  if (job && !isDataAgentVisibleOnJob(slug, job.status, job.appointmentType)) {
    return null;
  }

  if (!task || !agentId || !agentEntry) {
    return (
      <div className="flex min-h-full flex-1 flex-col p-6">
        <div className="-m-6 flex flex-1 flex-col gap-5 bg-muted px-6 pb-6 pt-6">
          <div>
            <Button
              variant="outline"
              size="sm"
              onClick={onBack}
              className="gap-1.5"
            >
              <ArrowLeft className="h-4 w-4" />
              {cameFromChat ? "Back to Chat" : "Back to Data"}
            </Button>
          </div>
          <div className="flex flex-1 flex-col items-center justify-center gap-4 py-24">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background">
              {agentEntry && (
                <agentEntry.icon className="h-8 w-8 text-muted-foreground" />
              )}
            </span>
            <div className="max-w-xl text-center">
              <h2 className="text-lg font-medium">
                {agentEntry
                  ? dataAgentLabel(agentEntry, isBankruptcy)
                  : "Agent"}
              </h2>
              <p className="text-sm text-muted-foreground">
                Waiting for documents. This agent runs automatically once a
                document in one of its trigger categories is indexed.
              </p>
            </div>
            {agentEntry?.supportsManualEntry && !isViewer && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                disabled={startTaskMutation.isPending}
                onClick={handleStartManualEntry}
              >
                <Plus className="h-4 w-4" />
                Enter manually
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Task exists — dispatch to the per-agent detail view. Wrapped in the
  // same p-6 shell as the index route so the views' `-m-6` bleed lines up.
  return (
    <div className="flex min-h-full flex-1 flex-col">
      <div className="flex min-h-full flex-1 flex-col p-6">
        <AgentDetailRouter
          agentId={agentId}
          taskId={task._id as Id<"summarizationTasks">}
          jobId={jobId as Id<"jobs">}
          summarizationRunId={
            task.summarizationRunId as Id<"summarizationRuns">
          }
          onBack={onBack}
          onRerun={handleRerun}
          isViewer={isViewer}
        />
      </div>
    </div>
  );
}
